/**
 * Catalogue des permissions au format `resource:action`.
 * Le contrôle d'accès (RBAC) côté serveur s'appuie sur ces clés,
 * et le frontend les utilise pour afficher/masquer les éléments d'UI.
 */
export const PERMISSIONS = [
    // Clients
    'customers:read',
    'customers:create',
    'customers:update',
    'customers:delete',
    // Véhicules
    'vehicles:read',
    'vehicles:create',
    'vehicles:update',
    'vehicles:delete',
    // Rendez-vous
    'appointments:read',
    'appointments:create',
    'appointments:update',
    'appointments:delete',
    // Ordres de réparation
    'repair_orders:read',
    'repair_orders:create',
    'repair_orders:update',
    'repair_orders:delete',
    // Pièces détachées / stock
    'parts:read',
    'parts:create',
    'parts:update',
    'parts:delete',
    'stock:read',
    'stock:move',
    // Fournisseurs
    'suppliers:read',
    'suppliers:create',
    'suppliers:update',
    'suppliers:delete',
    // Achats
    'purchases:read',
    'purchases:create',
    'purchases:update',
    'purchases:receive',
    // Ventes (caisse)
    'sales:read',
    'sales:create',
    // Facturation
    'invoices:read',
    'invoices:create',
    'invoices:update',
    'invoices:delete',
    'payments:read',
    'payments:create',
    // Employés / mécaniciens
    'employees:read',
    'employees:create',
    'employees:update',
    'employees:delete',
    // Rapports
    'reports:read',
    // Paramètres & administration
    'settings:read',
    'settings:update',
    'users:read',
    'users:manage',
];
/** Permissions accordées par défaut à chaque rôle (utilisé par le seed). */
export const DEFAULT_ROLE_PERMISSIONS = {
    // L'administrateur a tout.
    ADMIN: 'ALL',
    // Réceptionnaire : accueil, clients, véhicules, RDV, facturation basique.
    RECEPTIONIST: [
        'customers:read',
        'customers:create',
        'customers:update',
        'vehicles:read',
        'vehicles:create',
        'vehicles:update',
        'appointments:read',
        'appointments:create',
        'appointments:update',
        'appointments:delete',
        'repair_orders:read',
        'repair_orders:create',
        'invoices:read',
        'invoices:create',
        'payments:read',
        'payments:create',
        'reports:read',
    ],
    // Mécanicien : consultation clients/véhicules, gestion des ordres de réparation.
    MECHANIC: [
        'customers:read',
        'vehicles:read',
        'appointments:read',
        'appointments:update',
        'repair_orders:read',
        'repair_orders:create',
        'repair_orders:update',
        'parts:read',
        'stock:read',
    ],
    // Magasinier : pièces, stock, fournisseurs, achats.
    STOREKEEPER: [
        'parts:read',
        'parts:create',
        'parts:update',
        'parts:delete',
        'stock:read',
        'stock:move',
        'suppliers:read',
        'suppliers:create',
        'suppliers:update',
        'purchases:read',
        'purchases:create',
        'purchases:update',
        'purchases:receive',
        'sales:read',
        'sales:create',
    ],
    // Comptable : facturation, paiements, achats (lecture), rapports.
    ACCOUNTANT: [
        'customers:read',
        'invoices:read',
        'invoices:create',
        'invoices:update',
        'invoices:delete',
        'payments:read',
        'payments:create',
        'purchases:read',
        'sales:read',
        'reports:read',
        'settings:read',
    ],
};
/** Résout la liste effective de permissions d'un rôle (déplie 'ALL'). */
export function resolveRolePermissions(role) {
    const value = DEFAULT_ROLE_PERMISSIONS[role];
    return value === 'ALL' ? [...PERMISSIONS] : [...value];
}
// ---------------------------------------------------------------------------
//  Catalogue lisible (labels + regroupement par ressource) — sert à l'UI de
//  gestion des permissions et à l'endpoint /api/roles/permissions.
// ---------------------------------------------------------------------------
const RESOURCE_LABELS = {
    customers: 'Clients',
    vehicles: 'Véhicules',
    appointments: 'Rendez-vous',
    repair_orders: 'Ordres de réparation',
    parts: 'Pièces',
    stock: 'Stock',
    suppliers: 'Fournisseurs',
    purchases: 'Achats',
    sales: 'Ventes',
    invoices: 'Facturation',
    payments: 'Paiements',
    employees: 'Employés',
    reports: 'Rapports',
    settings: 'Paramètres',
    users: 'Utilisateurs',
};
const ACTION_LABELS = {
    read: 'Consulter',
    create: 'Créer',
    update: 'Modifier',
    delete: 'Supprimer',
    move: 'Mouvementer',
    receive: 'Réceptionner',
    manage: 'Gérer',
};
/** Libellé lisible d'une permission, ex. `users:manage` → « Gérer — Utilisateurs ». */
export function permissionLabel(key) {
    const [resource = '', action = ''] = key.split(':');
    return `${ACTION_LABELS[action] ?? action} — ${RESOURCE_LABELS[resource] ?? resource}`;
}
/** Permissions regroupées par ressource (dans l'ordre du catalogue). */
export const PERMISSION_GROUPS = (() => {
    const groups = new Map();
    for (const key of PERMISSIONS) {
        const [resource = '', action = ''] = key.split(':');
        if (!groups.has(resource)) {
            groups.set(resource, { resource, label: RESOURCE_LABELS[resource] ?? resource, permissions: [] });
        }
        groups.get(resource).permissions.push({ key, label: ACTION_LABELS[action] ?? action });
    }
    return [...groups.values()];
})();
//# sourceMappingURL=permissions.js.map